import { HeaderWithBar } from '../../HeaderWithBar/HeaderWithBar';
import { POSIcon } from '../../POSIcon/POSIcon';
import React, { useCallback, useMemo } from 'react';
import { Menu, Tab } from 'semantic-ui-react';
import {
  FilterGroup,
  FilterGroupItem,
} from '../../../interfaces/POSGroupFilter';
import { GroupFilterTabPane } from './GroupFilterTabPane';

import './GroupFilter.less';
import { POSGroupFilterType } from '../../../enums/POSGroupFilterType';
import { GroupFilterCustomTabPane } from './GroupFilterCustomTabPane';
import { adjustGroupId } from '../../../utils/GroupFilterHelper';

export interface Props {
  filterItems?: FilterGroupItem[];
  filterGroups: FilterGroup[];
  selectedFilterItems: FilterGroupItem[];
  activeTabIndex?: number;
  reset?: boolean;
  tabPaneLoadingText?: string;
  onReset?: () => void;
  onTabChange?: (
    selectedGroupId: string,
    activeIndex: string | number | undefined
  ) => void;
  onFilterChange?: (filterItem: FilterGroupItem, checked: boolean) => void;
}

export const GroupFilter = ({
  filterGroups,
  filterItems,
  selectedFilterItems,
  activeTabIndex,
  tabPaneLoadingText,
  onTabChange,
  onFilterChange,
}: Props) => {
  const getGroupName = useCallback(
    (group: FilterGroup) => {
      const selectedItemsCount = selectedFilterItems.filter(
        (item) => adjustGroupId(item.groupId) === group.groupId
      ).length;
      return selectedItemsCount > 0 ? (
        <>
          {group.groupName}{' '}
          <span className="selected-count">{selectedItemsCount}</span>
        </>
      ) : (
        group.groupName
      );
    },
    [selectedFilterItems]
  );

  const getFilterItems = useCallback(
    (filterGroup: FilterGroup) =>
      filterItems?.filter(
        (item) => adjustGroupId(item.groupId) === filterGroup.groupId
      ),
    [filterItems]
  );

  const panes = useMemo(() => {
    return filterGroups.map((filterGroup, index) => {
      const selectedItemsOfTabPane = selectedFilterItems.filter(
        (item) => adjustGroupId(item.groupId) === filterGroup.groupId
      );
      return {
        menuItem: (
          <Menu.Item key={filterGroup.groupId}>
            <div className="menu-item-name">
              {index == activeTabIndex ? (
                <HeaderWithBar>{getGroupName(filterGroup)}</HeaderWithBar>
              ) : (
                getGroupName(filterGroup)
              )}
            </div>
            <POSIcon icon="arrow-right" />
          </Menu.Item>
        ),
        render: () => {
          return filterGroup.groupType === POSGroupFilterType.Custom ? (
            <GroupFilterCustomTabPane
              filterItems={getFilterItems(filterGroup)}
              tabPaneLoadingText={tabPaneLoadingText}
            />
          ) : (
            <GroupFilterTabPane
              filterItems={getFilterItems(filterGroup)}
              filterGroup={filterGroup}
              selectedFilterItems={selectedItemsOfTabPane}
              tabPaneLoadingText={tabPaneLoadingText}
              onFilterChange={onFilterChange}
            />
          );
        },
      };
    });
  }, [
    filterGroups,
    selectedFilterItems,
    activeTabIndex,
    getGroupName,
    getFilterItems,
    tabPaneLoadingText,
    onFilterChange,
  ]);

  const handleTabChange = useCallback(
    (activeIndex: string | number | undefined) => {
      const activeGroup = filterGroups[(activeIndex as number) ?? 0];
      onTabChange?.(activeGroup.groupId, activeIndex);
    },
    [filterGroups, onTabChange]
  );

  return (
    <Tab
      className="grouped-filters"
      menu={{
        fluid: false,
        vertical: true,
        tabular: false,
      }}
      menuPosition="left"
      activeIndex={activeTabIndex}
      onTabChange={(_, { activeIndex }) => handleTabChange(activeIndex)}
      panes={panes}
    />
  );
};
